import React, { useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Filler, Tooltip } from "chart.js";
import "./fiyatGecmisi.css";

ChartJS.register(LinearScale, PointElement, LineElement, Filler, Tooltip);

/**
 * Detay sayfalarının fiyat geçmişi (Trendyol / Hepsiburada / Amazon ortak).
 *
 * Veri modeli (backend gecmisYaz): her urun_history satırı, fiyat DEĞİŞTİĞİ AN yazılan ESKİ fiyattır → fiyat, bir önceki
 * satırın kayitTarihi'nden bu satırın kayitTarihi'ne kadar geçerliydi. kayitTarihi her zaman ileri gider; fiyatTarihi
 * eski motorda çoğu zaman güncellenmediği için geri gidebiliyor (2026-11-01 canlıda 25 üründen 23'ü) → eski grafik
 * fiyatTarihi'ni etiket yapıp "Oca 2026'dan sonra 2025" gösteriyordu, noktalar da eşit aralıklıydı (3 gün süren fiyat 3 ay
 * sürenle aynı genişlikte). Burada: gerçek zaman ekseni, basamaklı çizgi (fiyat bir sonraki değişime kadar sabit),
 * dönem aralığı seçimi, ortalama süreye göre ağırlıklı.
 */

const GUN = 24 * 3600 * 1000;
const AYLAR = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const ARALIKLAR = [
  { ad: "1A", gun: 30 },
  { ad: "3A", gun: 90 },
  { ad: "6A", gun: 180 },
  { ad: "1Y", gun: 365 },
  { ad: "Tümü", gun: null },
];

const sayi = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
export const tl = (v) => (v == null || isNaN(v) ? "-" : sayi.format(v) + " TL");

const zaman = (t) => {
  const v = t ? new Date(t).getTime() : NaN;
  return isNaN(v) ? null : v;
};

const kisaTarih = (t, yilla) => {
  const d = new Date(t);
  return d.getDate() + " " + AYLAR[d.getMonth()] + (yilla ? " " + d.getFullYear() : "");
};

function sureMetni(ms) {
  const gun = Math.round(ms / GUN);
  if (gun < 1) return "1 günden kısa";
  if (gun < 45) return gun + " gün";
  const ay = Math.round(gun / 30);
  if (ay < 18) return ay + " ay";
  return (gun / 365).toFixed(1).replace(".", ",") + " yıl";
}

/** "20 ay" → "20 ayın" / "20 aylık" (gün → günün / günlük, yıl → yılın / yıllık). */
const iyelik = (m) => m + (m.endsWith("gün") ? "ün" : "ın");
const sifat = (m) => m + (m.endsWith("gün") ? "lük" : "lık");

/** urun_history + indirim kaydı → ardışık fiyat dönemleri [{bas, bit, fiyat}] (aynı fiyatlı komşular birleşir). */
export function donemleriKur(gecmis, urun, simdi, fiyatGizle) {
  const satirlar = (gecmis || [])
    .map((r) => ({ bit: zaman(r.kayitTarihi), fiyatTarihi: zaman(r.fiyatTarihi), fiyat: Number(r.discountedPrice) }))
    .filter((r) => r.bit && r.fiyat > 0)
    .sort((a, b) => a.bit - b.bit);
  const donemler = [];
  let bas = null;
  satirlar.forEach((r, i) => {
    if (i === 0) {
      const aday = r.fiyatTarihi || zaman(urun.takipBaslangicTarihi);
      bas = aday && aday < r.bit ? aday : r.bit - GUN;
    }
    if (r.bit > bas) {
      donemler.push({ bas, bit: r.bit, fiyat: r.fiyat });
    }
    bas = r.bit;
  });

  const yeni = Number(urun.yeniFiyat);
  if (yeni > 0) {
    let yakalanma = zaman(urun.kayitTarihi) || simdi;
    if (bas != null && yakalanma < bas) yakalanma = bas;
    if (bas == null) bas = yakalanma;
    if (bas < yakalanma && donemler.length) donemler[donemler.length - 1].bit = yakalanma; // arada kayıt yok: eski fiyat sürdü
    if (fiyatGizle) {
      // Amazon: güncel fiyat gösterilmez (ortaklık koşulu) → grafik yakalanma anında biter
      donemler.push({ bas: yakalanma, bit: yakalanma + GUN / 4, fiyat: yeni });
    } else {
      const kontrol = zaman(urun.durumKontrolTarihi);
      const guncel = Number(urun.guncelFiyat);
      if (urun.indirimDurumu === "SONA_ERDI" && guncel > 0 && kontrol && kontrol > yakalanma) {
        donemler.push({ bas: yakalanma, bit: kontrol, fiyat: yeni });
        donemler.push({ bas: kontrol, bit: Math.max(simdi, kontrol), fiyat: guncel, guncel: true });
      } else {
        donemler.push({ bas: yakalanma, bit: Math.max(simdi, yakalanma), fiyat: yeni, guncel: true });
      }
    }
  }

  const birlesik = [];
  donemler.forEach((d) => {
    const son = birlesik[birlesik.length - 1];
    if (son && son.fiyat === d.fiyat) {
      son.bit = d.bit;
      son.guncel = son.guncel || d.guncel;
    } else {
      birlesik.push({ ...d });
    }
  });
  return birlesik;
}

/** [bas, bit] aralığına kırpılmış dönemler. */
function kirp(donemler, bas) {
  if (bas == null) return donemler;
  return donemler
    .filter((d) => d.bit > bas)
    .map((d) => (d.bas < bas ? { ...d, bas } : d));
}

function istatistik(donemler) {
  if (!donemler.length) return null;
  let toplam = 0, sure = 0, enDusuk = donemler[0], enYuksek = donemler[0];
  donemler.forEach((d) => {
    const s = Math.max(d.bit - d.bas, 1);
    toplam += d.fiyat * s;
    sure += s;
    if (d.fiyat <= enDusuk.fiyat) enDusuk = d; // eşitlikte en yenisi
    if (d.fiyat > enYuksek.fiyat) enYuksek = d;
  });
  return { ortalama: toplam / sure, enDusuk, enYuksek };
}

/**
 * Şu anki (son) fiyattan daha düşüğü en son ne zaman görüldü: geriye doğru, fiyatı bundan büyük dönemler atlanır.
 * Dönen süre ≥ 7 gün ise "son X'in en düşük fiyatı" denir.
 */
function enDusukSuresi(donemler, simdi) {
  if (donemler.length < 2) return null;
  const son = donemler[donemler.length - 1];
  let i = donemler.length - 2;
  while (i >= 0 && donemler[i].fiyat > son.fiyat) i--;
  const baslangic = i >= 0 ? donemler[i].bit : donemler[0].bas;
  const sure = son.bas - baslangic;
  if (sure < 7 * GUN) return null;
  return { sure, hepsi: i < 0 };
}

/** Eksen işaretleri: aralığa göre gün / hafta / ay başları; Ocak ve ilk işaret yıllı (iki satır). */
function isaretler(min, max) {
  const gun = (max - min) / GUN;
  const sonuc = [];
  if (gun <= 75) {
    const adim = gun <= 10 ? 2 : gun <= 21 ? 4 : gun <= 45 ? 7 : 14;
    const d = new Date(min);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    for (; d.getTime() <= max; d.setDate(d.getDate() + adim)) sonuc.push(d.getTime());
    return sonuc;
  }
  const ay = gun / 30.4;
  const adim = ay <= 7 ? 1 : ay <= 14 ? 2 : ay <= 24 ? 3 : 6;
  const d = new Date(min);
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  d.setMonth(d.getMonth() + 1);
  while (d.getMonth() % adim !== 0) d.setMonth(d.getMonth() + 1);
  for (; d.getTime() <= max; d.setMonth(d.getMonth() + adim)) sonuc.push(d.getTime());
  return sonuc;
}


/**
 * @param gecmis    urunHistoryList
 * @param urun      urundetaygetir.urun (yeniFiyat, kayitTarihi, takipBaslangicTarihi, indirimDurumu, guncelFiyat, durumKontrolTarihi)
 * @param renk      mağaza rengi (çizgi)
 * @param fiyatGizle Amazon: güncel fiyat yazılmaz, grafik indirimin yakalandığı anda biter
 */
export default function FiyatGecmisi({ gecmis, urun, renk = "#f27a1a", fiyatGizle = false, urunAdi, magaza }) {
  // sunucu ile tarayıcı aynı "şimdi"yi kullansın diye ilk çizimde sabit (SSR uyumsuzluğu olmasın)
  const [simdi] = useState(() => Date.now());
  const tum = useMemo(() => (urun ? donemleriKur(gecmis, urun, simdi, fiyatGizle) : []), [gecmis, urun, simdi, fiyatGizle]);
  const toplamGun = tum.length ? (tum[tum.length - 1].bit - tum[0].bas) / GUN : 0;
  const secenekler = ARALIKLAR.filter((a) => a.gun == null || a.gun < toplamGun);
  const [aralik, setAralik] = useState(() => (toplamGun > 400 ? "1Y" : "Tümü"));
  const [secili, setSecili] = useState(null);
  const seciliRef = useRef(null);

  const son = tum.length ? tum[tum.length - 1].bit : simdi;
  const gunSayisi = (ARALIKLAR.find((a) => a.ad === aralik) || {}).gun;
  const gorunen = useMemo(() => kirp(tum, gunSayisi ? son - gunSayisi * GUN : null), [tum, gunSayisi, son]);
  const ist = useMemo(() => istatistik(gorunen), [gorunen]);
  const enDusuk = useMemo(() => enDusukSuresi(tum, simdi), [tum, simdi]);

  if (!urun || tum.length === 0) {
    return null;
  }

  const sonDonem = tum[tum.length - 1];
  const min = gorunen[0].bas;
  const max = gorunen[gorunen.length - 1].bit;
  const noktalar = gorunen.map((d) => ({ x: d.bas, y: d.fiyat }));
  noktalar.push({ x: max, y: gorunen[gorunen.length - 1].fiyat });
  const fiyatlar = gorunen.map((d) => d.fiyat);
  const yMin = Math.min(...fiyatlar), yMax = Math.max(...fiyatlar);
  const pay = Math.max((yMax - yMin) * 0.15, yMax * 0.03);

  const enDusukCizgisi = {
    id: "enDusukCizgisi",
    afterDatasetsDraw(chart) {
      const { ctx, chartArea, scales } = chart;
      const y = scales.y.getPixelForValue(ist.enDusuk.fiyat);
      ctx.save();
      ctx.strokeStyle = "rgba(22, 163, 74, 0.7)";
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      const hx = chart.$secX;
      if (hx != null && hx >= chartArea.left && hx <= chartArea.right) {
        ctx.setLineDash([]);
        ctx.strokeStyle = "rgba(39, 49, 66, 0.35)";
        ctx.beginPath();
        ctx.moveTo(hx, chartArea.top);
        ctx.lineTo(hx, chartArea.bottom);
        ctx.stroke();
      }
      ctx.restore();
    },
  };

  const sec = (event, _el, chart) => {
    const x = chart.scales.x.getValueForPixel(event.x);
    const d = event.type === "mouseout" || x < min || x > max ? null : gorunen.find((p) => x >= p.bas && x <= p.bit) || null;
    chart.$secX = d ? event.x : null;
    chart.draw();
    if (seciliRef.current !== d) {
      seciliRef.current = d;
      setSecili(d);
    }
  };

  const veri = {
    datasets: [{
      data: noktalar,
      stepped: "after",
      borderColor: renk,
      borderWidth: 2,
      fill: true,
      backgroundColor: (c) => {
        const { ctx, chartArea } = c.chart;
        if (!chartArea) return "transparent";
        const g = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        g.addColorStop(0, renk + "33");
        g.addColorStop(1, renk + "05");
        return g;
      },
      pointRadius: (c) => (c.dataIndex === noktalar.length - 1 && !fiyatGizle && sonDonem.guncel ? 4 : 0),
      pointBackgroundColor: renk,
      pointHoverRadius: 0,
    }],
  };

  const secenek = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    parsing: false,
    events: ["mousemove", "mouseout", "touchstart", "touchmove", "click"],
    onHover: sec,
    onClick: sec,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    layout: { padding: { right: 6 } },
    scales: {
      x: {
        type: "linear",
        min,
        max,
        grid: { display: false },
        border: { color: "#e3e6ea" },
        afterBuildTicks: (eksen) => { eksen.ticks = isaretler(min, max).map((value) => ({ value })); },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          color: "#68727e",
          font: { size: 11 },
          callback: (v, i) => {
            const d = new Date(v);
            if (max - min <= 75 * GUN) return kisaTarih(v, false);
            return d.getMonth() === 0 || i === 0 ? [AYLAR[d.getMonth()], String(d.getFullYear())] : AYLAR[d.getMonth()];
          },
        },
      },
      y: {
        min: Math.max(0, yMin - pay),
        max: yMax + pay,
        grid: { color: "#f0f1f3" },
        border: { display: false },
        ticks: { color: "#68727e", font: { size: 11 }, maxTicksLimit: 5, includeBounds: false, callback: (v) => sayi.format(Math.round(v)) },
      },
    },
  };

  const bilgi = secili
    ? <span><b>{tl(secili.fiyat)}</b> · {kisaTarih(secili.bas, true)} – {secili.guncel ? "bugün" : kisaTarih(secili.bit, true)} <span style={{ color: "#68727e" }}>({sureMetni(secili.bit - secili.bas)})</span></span>
    : <span style={{ color: "#68727e" }}>Grafiğe dokun ya da üzerine gel: o tarihteki fiyat</span>;

  const ucuzYuzde = ist && sonDonem.fiyat < ist.ortalama ? Math.round((1 - sonDonem.fiyat / ist.ortalama) * 100) : 0;

  // fiyat değişim tablosu: en yeniden eskiye, bir öncekine göre ↑ / ↓
  const degisimler = tum.map((d, k) => ({ ...d, yon: k === 0 ? 0 : Math.sign(d.fiyat - tum[k - 1].fiyat) })).reverse();
  const tumIst = istatistik(tum);
  const analiz = analizMetni(urunAdi, magaza, tum, tumIst, fiyatGizle);

  return (
    <section className="fg">
      <div className="fg-ust">
        <h2 className="fg-baslik"><span aria-hidden="true" style={{ color: renk }}>〽</span> Fiyat geçmişi</h2>
        {secenekler.length > 1 && (
          <div role="group" aria-label="Zaman aralığı" className="fg-araliklar">
            {secenekler.map((a) => (
              <button key={a.ad} onClick={() => { setAralik(a.ad); setSecili(null); seciliRef.current = null; }}
                aria-pressed={aralik === a.ad} className="fg-aralik"
                style={aralik === a.ad ? { background: renk, borderColor: renk, color: "#fff" } : undefined}>{a.ad}</button>
            ))}
          </div>
        )}
      </div>

      {enDusuk && !fiyatGizle && (
        <div className="fg-rozet">
          📉 <b>{enDusuk.hepsi ? "Takip ettiğimiz " + sifat(sureMetni(sonDonem.bas - tum[0].bas)) + " sürenin" : "Son " + iyelik(sureMetni(enDusuk.sure))} en düşük fiyatı</b>
          {ucuzYuzde >= 3 && <span> · ortalamadan %{ucuzYuzde} ucuz</span>}
        </div>
      )}

      <div className="fg-govde">
        <div className="fg-grafik">
          <div className="fg-bilgi">{bilgi}</div>
          <div style={{ position: "relative", height: 250, touchAction: "pan-y" }}>
            <Line data={veri} options={secenek} plugins={[enDusukCizgisi]} />
          </div>
          <div className="fg-lejant">
            <span><span style={{ display: "inline-block", width: 14, borderTop: "2px solid " + renk, verticalAlign: "middle", marginRight: 4 }} />Fiyat</span>
            <span><span style={{ display: "inline-block", width: 14, borderTop: "1px dashed #16a34a", verticalAlign: "middle", marginRight: 4 }} />En düşük</span>
            <span>Takip başlangıcı: {kisaTarih(tum[0].bas, true)}</span>
          </div>
        </div>
        <div className="fg-tablo" role="table" aria-label="Fiyat değişimleri">
          <div className="fg-satir fg-satir-baslik" role="row"><span>Tarih</span><span>Fiyat</span></div>
          <div className="fg-satirlar">
            {degisimler.map((d, k) => (
              <div key={d.bas} className={"fg-satir" + (k === 0 ? " fg-satir-son" : "")} role="row">
                <span>{new Date(d.bas).toLocaleDateString("tr-TR")}</span>
                <span>
                  {d.yon < 0 && <span className="fg-ok fg-ok-asagi" aria-label="düştü">↓</span>}
                  {d.yon > 0 && <span className="fg-ok fg-ok-yukari" aria-label="arttı">↑</span>}
                  <b>{tl(d.fiyat)}</b>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {ist && (
        <div className="fg-istatistik">
          <div><span>En düşük</span><b style={{ color: "#16a34a" }}>{tl(ist.enDusuk.fiyat)}</b><small>{ist.enDusuk.guncel ? "şu an" : kisaTarih(ist.enDusuk.bas, true)}</small></div>
          {!fiyatGizle && <div><span>Güncel</span><b style={{ color: renk }}>{tl(sonDonem.fiyat)}</b><small>{sonDonem.guncel ? "şu an" : ""}</small></div>}
          <div><span>Ortalama</span><b style={{ color: "#6d28d9" }}>{tl(Math.round(ist.ortalama * 100) / 100)}</b><small>süreye göre</small></div>
          <div><span>En yüksek</span><b style={{ color: "#dc2626" }}>{tl(ist.enYuksek.fiyat)}</b><small>{kisaTarih(ist.enYuksek.bas, true)}</small></div>
        </div>
      )}

      {analiz && (
        <div className="fg-analiz">
          <h3>Fiyat analizi</h3>
          <p>{analiz}</p>
        </div>
      )}
    </section>
  );
}

/** Otomatik fiyat analizi paragrafı (onual'daki gibi; sayfaya metin içeriği de kazandırır). Amazon'da güncel fiyat yazılmaz. */
function analizMetni(ad, magaza, tum, ist, fiyatGizle) {
  if (!ad || !ist || tum.length < 2) return null;
  const son = tum[tum.length - 1];
  const sure = sureMetni(son.bit - tum[0].bas);
  const fark = Math.round((son.fiyat / ist.ortalama - 1) * 100);
  const yon = fark < 0 ? "%" + (-fark) + " altında" : fark > 0 ? "%" + fark + " üstünde" : "ortalamasıyla aynı seviyede";
  const parcalar = [];
  if (fiyatGizle) {
    parcalar.push(`${ad}, ${magaza}'da yaklaşık ${sure}dır takip ediliyor; yakaladığımız indirimli fiyat bu dönemin ortalamasının ${yon}.`);
  } else {
    parcalar.push(`${ad}, şu anda ${magaza}'da ${tl(son.fiyat)} fiyatıyla satılıyor. Bu fiyat, takip ettiğimiz yaklaşık ${sure}lık dönemdeki ${tl(Math.round(ist.ortalama))} ortalama fiyatın ${yon}.`);
  }
  parcalar.push(`Ürün bu süre boyunca en düşük ${tl(ist.enDusuk.fiyat)} ile en yüksek ${tl(ist.enYuksek.fiyat)} arasında fiyatlandı ve ${tum.length - 1} kez fiyat değiştirdi.`);
  const ucAy = tum.filter((d) => d.bit >= son.bit - 90 * GUN);
  if (ucAy.length >= 3) {
    const ilk = ucAy[0].fiyat;
    const egilim = son.fiyat < ilk * 0.97 ? "düşüş" : son.fiyat > ilk * 1.03 ? "artış" : "yatay bir seyir";
    parcalar.push(`Son 3 ayda fiyatında ${egilim} ${egilim === "yatay bir seyir" ? "var" : "eğilimi görülüyor"}.`);
  }
  return parcalar.join(" ");
}
